import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Stack,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Mail, Check, X, Eye, EyeOff } from 'lucide-react';
import { post, get } from '../utility/fetch';
import notification from '../utility/notification';
import { motion, AnimatePresence } from 'framer-motion';

// Define the steps for the password reset process
const steps = ['Enter Email', 'Verify OTP', 'Reset Password'];

// Main ForgotPasswordPage component
export default function ForgotPasswordPage({ isModal = false, onClose }) {
  const navigate = useNavigate();
  // State to manage the current step of the password reset process
  const [activeStep, setActiveStep] = useState(0);
  // State to manage loading indicators
  const [isLoading, setIsLoading] = useState(false);
  // State to store form data
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    password: '',
    confirmPassword: ''
  });
  // State for password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // State to track if passwords match
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Password validation logic
  const validatePassword = (password) => {
    return {
      hasLength: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  };

  // Sync passwordsMatch state whenever password fields change
  useEffect(() => {
    if (formData.confirmPassword) {
      setPasswordsMatch(formData.password === formData.confirmPassword);
    } else {
      setPasswordsMatch(true);
    }
  }, [formData.password, formData.confirmPassword]);

  // Handles form submission and progression through steps
  const handleNext = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (activeStep === 0) {
        // Send OTP
        const response = await post(`/auth/forget-password/${formData.email}`);
        if (response?.statusCode === 200) {
          setActiveStep(1);
          notification({ type: 'success', message: 'OTP sent successfully to your email' });
        }
      } else if (activeStep === 1) {
        // Verify OTP
        const res = await get(`/auth/validateotp?otp=${formData.otp}&email=${formData.email}`);
        if (res) {
          setActiveStep(2);
          notification({ type: 'success', message: 'Email verified!' });
        }
      } else {
        // Reset Password
        const passwordChecks = validatePassword(formData.password);
        if (!Object.values(passwordChecks).every(Boolean)) {
          notification({ type: 'error', message: 'Please meet all password requirements' });
          return;
        }
        if (!passwordsMatch) {
          notification({ type: 'error', message: 'Passwords do not match' });
          return;
        }

        const res = await post(`/auth/reset-password?email=${formData.email}`, {
          password: formData.password,
          confirmPassword: formData.confirmPassword
        });

        if (res?.statusCode === 200) {
          notification({ type: 'success', message: 'Password reset successful!' });
          sessionStorage.setItem('token', res.data.token);
          sessionStorage.setItem('name', res.data.firstName + ' ' + res.data.lastName);
          sessionStorage.setItem('healthCareProviderId', res.data.healthCareProviderId);
          sessionStorage.setItem('role', res.data.roles[0]?.name);
          sessionStorage.setItem('userName', res.data.userName);
          sessionStorage.setItem('userId', res.data.userId);
          sessionStorage.setItem('userType', res.data.usersType);
          notification({ message: "Login successful", type: "success" });
          navigate('/landing');
        }
      }
    } catch (error) {
      notification({ type: 'error', message: error.message || 'Something went wrong' });
    } finally {
      setIsLoading(false);
    }
  };

  // Renders the content of the current step
  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            required
            sx={{ mt: 2 }}
          />
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              An OTP has been sent to <strong>{formData.email}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Enter OTP"
              value={formData.otp}
              onChange={(e) => setFormData(prev => ({ ...prev, otp: e.target.value }))}
              required
            />
          </Box>
        );
      case 2:
        const passwordChecks = validatePassword(formData.password);
        return (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(prev => !prev)}
                      edge="end"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              required
              error={!passwordsMatch}
              helperText={!passwordsMatch ? 'Passwords do not match' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(prev => !prev)}
                      edge="end"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Password Requirements:
              </Typography>
              {[
                { check: passwordChecks.hasLength, label: 'At least 8 characters' },
                { check: passwordChecks.hasUpper, label: 'One uppercase letter' },
                { check: passwordChecks.hasLower, label: 'One lowercase letter' },
                { check: passwordChecks.hasNumber, label: 'One number' },
                { check: passwordChecks.hasSpecial, label: 'One special character' },
              ].map(({ check, label }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {check ? <Check size={16} color="green" /> : <X size={16} color="red" />}
                  <Typography variant="body2" color={check ? 'success.main' : 'error.main'}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="sm" sx={isModal ? { p: 0 } : { mt: 8 }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeStep}
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -50, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Paper sx={{ p: 4 }}>
            {!isModal && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <img
                  src={'/ConnectHealthPro.png'}
                  alt="ConnectHealthPro"
                  style={{ height: 60, marginBottom: 16 }}
                />
                <Typography variant="h5" sx={{ mb: 1, color: 'primary.main' }}>
                  Password Reset
                </Typography>
                <Button variant="text" onClick={() => navigate('/login')} sx={{ fontSize: '0.875rem' }}>
                  ← Back to Login
                </Button>
              </Box>
            )}

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box component="form" onSubmit={handleNext}>
              {getStepContent()}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                  startIcon={isLoading && <CircularProgress size={20} />}
                >
                  {activeStep === steps.length - 1 ? 'Reset Password' : 'Next'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </AnimatePresence>
    </Container>
  );
}
